import { useState } from 'react'
import { useSiteConfig } from './useSiteConfig'
import { useMarkets, type Market } from './useMarkets'
import { Logo } from './Logo'

const CHAIN_ID = 4663
const PROTOCOL_FEE_BPS = 10
const KEEPER_TIP_BPS = 5
const fBps = (b: number) => (b / 100).toFixed(2).replace(/\.00$/, '') + '%'

const card: React.CSSProperties = {
  background: 'rgba(13,17,29,.66)',
  border: '1px solid rgba(154,163,199,.14)',
  borderRadius: 18,
  backdropFilter: 'blur(16px)',
}
const mono = "'Geist Mono',monospace"
const label: React.CSSProperties = {
  fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase', color: '#7c86ad',
}

export function App() {
  const config = useSiteConfig()
  const markets = useMarkets()

  return (
    <div style={{ minHeight: '100vh', position: 'relative', paddingBottom: 64, overflowX: 'clip' }}>
      <Blobs />
      <Header twitterUrl={config?.twitterUrl} />
      <main
        style={{
          position: 'relative', zIndex: 1, maxWidth: 1440, margin: '0 auto',
          padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 28,
        }}
      >
        <Hero coinAddress={config?.coinAddress ?? null} loading={config === null} />
        <Ticker markets={markets} />
        <Diagram />
        <Steps />
      </main>
      <Footer twitterUrl={config?.twitterUrl} />
    </div>
  )
}

function Blobs() {
  return (
    <div aria-hidden style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -180, left: -120, width: 640, height: 640, borderRadius: '50%', background: 'radial-gradient(circle,rgba(163,255,91,.16),transparent 65%)', filter: 'blur(30px)', animation: 'blobA 38s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', top: '30%', right: -200, width: 720, height: 720, borderRadius: '50%', background: 'radial-gradient(circle,rgba(77,225,255,.14),transparent 65%)', filter: 'blur(30px)', animation: 'blobB 44s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', bottom: -240, left: '35%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle,rgba(192,132,252,.13),transparent 65%)', filter: 'blur(30px)', animation: 'blobA 52s ease-in-out infinite reverse' }} />
    </div>
  )
}

function XMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function Header({ twitterUrl }: { twitterUrl?: string }) {
  return (
    <header
      style={{
        position: 'sticky', top: 0, zIndex: 20, height: 64, background: 'rgba(7,9,15,.72)',
        backdropFilter: 'blur(18px)', borderBottom: '1px solid rgba(154,163,199,.12)',
      }}
    >
      <div style={{ maxWidth: 1440, margin: '0 auto', height: 64, padding: '0 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo size={26} id="mark-header" />
          <span style={{ fontWeight: 700, fontSize: 18, letterSpacing: '-.01em' }}>Ballast</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {twitterUrl && (
            <a
              href={twitterUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Ballast on X"
              style={{ display: 'grid', placeItems: 'center', width: 32, height: 32, borderRadius: 999, border: '1px solid rgba(154,163,199,.25)', background: 'rgba(238,242,255,.06)', color: '#eef2ff' }}
            >
              <XMark size={14} />
            </a>
          )}
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: mono, fontSize: 12,
              padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(192,132,252,.4)',
              color: '#c084fc', background: 'rgba(192,132,252,.12)', whiteSpace: 'nowrap',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#c084fc' }} />
            Robinhood Chain · {CHAIN_ID}
          </span>
        </div>
      </div>
    </header>
  )
}

function Hero({ coinAddress, loading }: { coinAddress: string | null; loading: boolean }) {
  const [copied, setCopied] = useState(false)
  const has = !!coinAddress

  return (
    <section style={{ padding: '56px 0 8px', display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 820 }}>
      <span style={{ fontFamily: mono, fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', color: '#7c86ad' }}>
        Robinhood Chain · 4663 · sibling of StockDCA
      </span>

      <div
        style={{
          ...card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px',
          padding: '12px 16px', borderRadius: 14, maxWidth: 640,
        }}
      >
        <span style={{ ...label, flexShrink: 0 }}>$BLST contract · 4663</span>
        <span
          style={{
            fontFamily: mono, fontSize: 13, color: has ? '#eef2ff' : '#7c86ad',
            overflowWrap: 'anywhere', minWidth: 0, flex: '1 1 240px',
          }}
        >
          {loading ? 'Reading…' : has ? coinAddress : 'Contract address published at launch'}
        </span>
        {has && (
          <button
            onClick={() => {
              navigator.clipboard?.writeText(coinAddress!)
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            }}
            style={{
              flexShrink: 0, fontFamily: mono, fontSize: 12, padding: '6px 10px', borderRadius: 8,
              border: '1px solid rgba(154,163,199,.25)', background: 'rgba(238,242,255,.06)', color: '#eef2ff',
            }}
          >
            {copied ? 'copied' : 'copy'}
          </button>
        )}
      </div>

      <h1 style={{ margin: 0, fontSize: 'clamp(36px,6vw,64px)', lineHeight: 1.02, letterSpacing: '-.03em', fontWeight: 700, textWrap: 'pretty' }}>
        Rebalancing that never holds your tokens.
      </h1>
      <p style={{ margin: 0, fontSize: 18, lineHeight: 1.5, color: '#9aa3c7', maxWidth: 640, textWrap: 'pretty' }}>
        Set target weights across listed stock tokens and a drift tolerance. When the basket drifts past it, a keeper trades
        the overweight tokens into the underweight ones through Uniswap V3 and returns everything to your wallet in the same
        transaction. Nothing is deposited, escrowed or held.
      </p>
    </section>
  )
}

function Ticker({ markets }: { markets: Market[] | null }) {
  // Null = loading, empty = the read failed. Neither should invent a price.
  if (!markets || markets.length === 0) return null
  const row = [...markets, ...markets]

  return (
    <>
      <div
        style={{
          overflow: 'hidden', borderTop: '1px solid rgba(154,163,199,.12)',
          borderBottom: '1px solid rgba(154,163,199,.12)', padding: '12px 0',
          maskImage: 'linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent)',
          WebkitMaskImage: 'linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent)',
        }}
      >
        <div style={{ display: 'flex', gap: 40, width: 'max-content', animation: 'tick 48s linear infinite' }}>
          {row.map((m, i) => {
            const priced = m.ethPerShare != null && m.ethPerShare > 0
            return (
              <div key={`${m.address}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: mono, fontSize: 13, flexShrink: 0 }}>
                <span style={{ color: '#eef2ff', fontWeight: 600 }}>{m.sym}</span>
                <span style={{ color: '#9aa3c7' }}>{priced ? `${m.ethPerShare!.toFixed(5)} Ξ` : '—'}</span>
                <span
                  style={{
                    fontSize: 11, padding: '2px 6px', borderRadius: 6,
                    color: priced ? '#a3ff5b' : '#7c86ad',
                    background: priced ? 'rgba(163,255,91,.12)' : 'rgba(154,163,199,.12)',
                  }}
                >
                  {priced ? `pool ${(m.fee / 10000).toFixed(2)}%` : m.state}
                </span>
              </div>
            )
          })}
        </div>
      </div>
      <p style={{ margin: '-14px 0 0', fontSize: 13, color: '#7c86ad', maxWidth: 820 }}>
        Live Uniswap V3 pool prices on Robinhood Chain, at the 5-minute TWAP. A stock token is an on-chain token tracking an
        equity; holding one is not ownership of the underlying share.
      </p>
    </>
  )
}

function Diagram() {
  return (
    <div style={{ ...card, borderRadius: 22, boxShadow: '0 24px 60px rgba(0,0,0,.45)', padding: 28, display: 'flex', flexDirection: 'column', gap: 22 }}>
      <span style={label}>One transaction, start to finish</span>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 14 }}>
        <Cell n="1 · YOUR WALLET" body="Holds every token, before and after." accent="#a3ff5b" />
        <Cell n="2 · BALLAST" body="Pulls only the overweight amount. Holds nothing between blocks." accent="#9aa3c7" dashed />
        <Cell n="3 · UNISWAP V3" body="Swaps bounded by the 5-minute TWAP." accent="#4de1ff" />
        <Cell n="4 · YOUR WALLET" body="Receives the underweight tokens in the same transaction." accent="#a3ff5b" />
      </div>
      <p style={{ margin: 0, color: '#9aa3c7', fontSize: 15, lineHeight: 1.5, textWrap: 'pretty' }}>
        Worst case if the contract is paused, abandoned or compromised: the basket stops being maintained. Your tokens were
        never inside it.
      </p>
    </div>
  )
}

function Cell({ n, body, accent, dashed }: { n: string; body: string; accent: string; dashed?: boolean }) {
  const tint = dashed ? 'rgba(238,242,255,.04)' : `${accent}14`
  return (
    <div
      style={{
        borderRadius: 14, padding: 16, background: tint,
        border: dashed ? '1px dashed rgba(154,163,199,.35)' : `1px solid ${accent}4d`,
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
    >
      <span style={{ fontFamily: mono, fontSize: 11, color: accent }}>{n}</span>
      <span style={{ fontSize: 14, color: '#eef2ff', lineHeight: 1.4 }}>{body}</span>
    </div>
  )
}

function Steps() {
  const steps: Array<[string, string, React.ReactNode]> = [
    ['01', 'Set targets', 'Pick listed tokens, weights that sum to 100%, a tolerance and a minimum interval.'],
    ['02', 'Grant permissions', 'One bounded allowance per token. An approval is a permission, not a transfer. Revoke any time.'],
    ['03', 'Keepers watch drift', 'When any weight leaves the band, anyone can call rebalance(id) for a tip.'],
    [
      '04',
      'Pay per trade',
      <>
        Fee on rebalanced volume: <span style={{ fontFamily: mono, color: '#eef2ff' }}>{fBps(PROTOCOL_FEE_BPS + KEEPER_TIP_BPS)}</span>{' '}
        (protocol {fBps(PROTOCOL_FEE_BPS)} + keeper tip {fBps(KEEPER_TIP_BPS)}), plus Uniswap pool fees.
      </>,
    ],
  ]
  return (
    <div id="how" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16 }}>
      {steps.map(([n, title, body]) => (
        <div key={n} style={{ ...card, padding: 22, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontFamily: mono, color: '#c084fc', fontSize: 12 }}>{n}</span>
          <span style={{ fontWeight: 600, fontSize: 17 }}>{title}</span>
          <span style={{ color: '#9aa3c7', fontSize: 14, lineHeight: 1.5 }}>{body}</span>
        </div>
      ))}
    </div>
  )
}

function Footer({ twitterUrl }: { twitterUrl?: string }) {
  return (
    <footer
      style={{
        position: 'relative', zIndex: 1, maxWidth: 1440, margin: '0 auto',
        padding: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 12, textAlign: 'center',
      }}
    >
      {twitterUrl && (
        <a href={twitterUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: '#9aa3c7', fontFamily: mono, fontSize: 12 }}>
          <XMark size={12} />
          {twitterUrl.replace(/^https:\/\/(www\.)?/, '')}
        </a>
      )}
      <div style={{ fontSize: 12, color: '#7c86ad', maxWidth: 640 }}>
        A stock token is an on-chain token tracking an equity; holding one is not ownership of the underlying share.
        Nothing here is a forecast.
      </div>
    </footer>
  )
}
